import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { UsersThree, PencilSimple, CurrencyDollar, Prohibit, CheckCircle, X, Eye, UserPlus, Copy, Phone, Trash, SignIn } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["user", "org_admin", "engineer", "reseller", "master_distributor", "provider", "admin", "platform_owner"];
const emptyNew = { name: "", email: "", phone: "", role: "user", credit_dollars: "", mode: "invite", temp_password: "" };

function Section({ title, items }) {
  return (
    <div>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
      {items.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : (
        <div className="space-y-1">
          {items.slice(0, 8).map((it, i) => (<div key={i} className="truncate rounded-lg bg-white/5 px-2 py-1 text-xs">{it}</div>))}
        </div>
      )}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="admin-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold">{title}</h3>
          <button data-testid="modal-close" onClick={onClose} className="rounded-full p-1 hover:bg-white/10"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data } = useQuery({ queryKey: ["admin-users"], queryFn: async () => (await api.get("/admin/users")).data });
  const users = data?.users || [];
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;
  const statusOf = (u) => u.pending ? "pending" : (["blocked", "suspended"].includes(u.status || "active") ? "suspended" : "active");
  const counts = { all: users.length, active: 0, pending: 0, suspended: 0 };
  users.forEach((u) => { const s = statusOf(u); counts[s] = (counts[s] || 0) + 1; });
  const filtered = users.filter((u) => {
    if (statusFilter !== "all" && statusOf(u) !== statusFilter) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [String(u.member_no ?? ""), u.name, u.email, u.phone, ...(u.phones || [])]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const pendingInView = filtered.filter((u) => u.pending);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const paged = filtered.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE);
  const allPendingSelected = pendingInView.length > 0 && pendingInView.every((u) => selected.includes(u.email));
  const toggleSelect = (email) => setSelected((s) => s.includes(email) ? s.filter((e) => e !== email) : [...s, email]);
  const toggleSelectAll = () => setSelected(allPendingSelected
    ? selected.filter((e) => !pendingInView.some((u) => u.email === e))
    : [...new Set([...selected, ...pendingInView.map((u) => u.email)])]);

  const [editing, setEditing] = useState(null);
  const [crediting, setCrediting] = useState(null);
  const [detail, setDetail] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assignForm, setAssignForm] = useState({ number: "", country: "US" });
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newUser, setNewUser] = useState(emptyNew);
  const [created, setCreated] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkRole, setBulkRole] = useState("user");
  const [bulkResult, setBulkResult] = useState(null);

  const roleOptions = me?.role === "platform_owner" ? ROLES : ROLES.filter((r) => !["admin", "platform_owner"].includes(r));

  const [form, setForm] = useState({ name: "", role: "user", status: "active" });
  const [credit, setCredit] = useState({ amount: "", direction: "add", reason: "" });

  const openEdit = (u) => { setForm({ name: u.name || "", role: u.role, status: u.status || "active", email: u.email || "", notify_phone: u.notify_phone || "" }); setEditing(u); };
  const openCredit = (u) => { setCredit({ amount: "", direction: "add", reason: "" }); setCrediting(u); };

  const openDetail = async (u) => {
    try { const { data } = await api.get(`/admin/users/${u.id}/detail`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const canImpersonate = ["platform_owner", "admin", "engineer"].includes(me?.role);
  const isEngineer = me?.role === "engineer";
  const impersonate = async (u) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${u.id}`);
      localStorage.setItem("nexus_staff_token", localStorage.getItem("nexus_token") || "");
      localStorage.setItem("nexus_token", data.token);
      localStorage.setItem("nexus_impersonation", JSON.stringify(data.customer));
      window.location.href = "/app/telephony";
    } catch (e) { toast.error(apiError(e)); }
  };

  const openAssign = (u) => { setAssignForm({ number: "", country: "US" }); setAssigning(u); };
  const assignNumber = async () => {
    if (!assignForm.number.trim()) { toast.error("Enter a phone number"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${assigning.id}/assign-number`, { number: assignForm.number.trim(), country: (assignForm.country || "US").trim() });
      toast.success(`Number assigned to ${assigning.name || assigning.email}`);
      setAssigning(null); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const createUser = async () => {
    if (!newUser.name.trim() || !newUser.email.trim()) { toast.error("Name and email are required"); return; }
    if (newUser.mode === "temp_password" && newUser.temp_password && newUser.temp_password.length < 8) {
      toast.error("Temporary password must be at least 8 characters"); return;
    }
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/users", {
        name: newUser.name.trim(), email: newUser.email.trim(), phone: newUser.phone.trim() || undefined,
        role: newUser.role, credit_dollars: parseFloat(newUser.credit_dollars || "0") || 0,
        mode: newUser.mode, temp_password: newUser.mode === "temp_password" ? (newUser.temp_password || undefined) : undefined,
      });
      toast.success("Customer created");
      setCreated({ email: newUser.email.trim(), ...res });
      setNewUser(emptyNew); setAdding(false); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const bulkInvite = async () => {
    const emails = bulkText.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) { toast.error("Paste at least one email"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/users/bulk", { emails, role: bulkRole });
      toast.success(`${res.created.length} invited · ${res.skipped.length} skipped`);
      setBulkResult(res); setBulkText(""); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${editing.id}`, form);
      toast.success("Customer updated");
      setEditing(null); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const saveCredit = async () => {
    const cents = Math.round(parseFloat(credit.amount) * 100);
    if (!cents || cents <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/users/${crediting.id}/credit`, {
        amount_cents: cents, direction: credit.direction, reason: credit.reason || "Manual adjustment",
      });
      toast.success(`${credit.direction === "add" ? "Added" : "Subtracted"} ${money(cents)} · new balance ${money(res.balance_cents)}`);
      setCrediting(null); refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (u, status) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { status });
      toast.success(status === "active" ? "Customer activated" : "Customer suspended");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const terminateUser = async (u) => {
    if (!window.confirm(`Permanently TERMINATE ${u.email}?\n\nThis deletes their account and wallet and releases any numbers. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Terminated ${u.email}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const activatePending = async (u) => {
    if (!window.confirm(`Activate ${u.email}?\n\nTheir account goes live immediately with the password they chose at sign-up.`)) return;
    try {
      await api.post("/admin/users/activate-pending", { email: u.email });
      toast.success(`Activated ${u.email}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deletePending = async (u) => {
    if (!window.confirm(`Remove the pending sign-up for ${u.email}? This deletes their unfinished sign-up.`)) return;
    try {
      await api.post("/admin/users/delete-pending", { email: u.email });
      toast.success(`Removed ${u.email}`);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const bulkActivate = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Activate ${selected.length} pending customer(s)?\n\nEach account goes live with the password they chose at sign-up.`)) return;
    try {
      const { data } = await api.post("/admin/users/activate-pending-bulk", { emails: selected });
      toast.success(`Activated ${data.activated}${data.failed ? ` · ${data.failed} failed` : ""}`);
      setSelected([]); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">Add, edit, block, and adjust paid balance. Added credit is real funds — usable for live calls & purchases.</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:shrink-0">
          {!isEngineer && (
            <>
              <button data-testid="bulk-invite-btn" onClick={() => { setBulkResult(null); setBulk(true); }}
                className="rounded-full border border-border px-4 py-2.5 text-sm font-semibold hover:bg-white/5">Bulk invite</button>
              <button data-testid="add-customer-btn" onClick={() => { setNewUser(emptyNew); setAdding(true); }}
                className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
                <UserPlus size={16} weight="bold" /> Add customer
              </button>
            </>
          )}
        </div>
      </div>

      <div className="relative">
        <input data-testid="customer-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search by member #, name, email or phone…"
          className="w-full rounded-xl border border-white/10 bg-card px-4 py-2.5 text-sm outline-none focus:border-primary" />
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="status-filters">
        {[["all", "All"], ["active", "Active"], ["pending", "Pending"], ["suspended", "Suspended"]].map(([key, lbl]) => {
          const on = statusFilter === key;
          const onCls = key === "pending" ? "bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/50"
            : key === "suspended" ? "bg-red-500/20 text-red-300 ring-1 ring-red-500/50"
            : key === "active" ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/50"
            : "bg-primary text-primary-foreground";
          return (
            <button key={key} data-testid={`filter-${key}`} onClick={() => { setStatusFilter(key); setSelected([]); setPage(1); }}
              className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${on ? onCls : "bg-white/5 text-muted-foreground hover:bg-white/10"}`}>
              {lbl} <span className="ml-1 opacity-70">{counts[key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {pendingInView.length > 0 && !isEngineer && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary/[0.05] px-3 py-2" data-testid="bulk-bar">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="select-all-pending" checked={allPendingSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-primary" />
            {selected.length > 0 ? `${selected.length} selected` : `Select all ${pendingInView.length} pending`}
          </label>
          <button data-testid="bulk-activate-btn" disabled={!selected.length} onClick={bulkActivate}
            className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground disabled:opacity-40">
            <CheckCircle size={15} weight="bold" /> Activate selected
          </button>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-white/10 bg-card">
        <div className="hidden grid-cols-12 gap-2 border-b border-white/10 px-4 py-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground md:grid">
          <div className="col-span-2">ID</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-2">Email</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2 text-right">Balance (paid)</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {paged.map((u) => {
          const suspended = ["blocked", "suspended"].includes(u.status || "active");
          const statusKey = u.pending ? "pending" : (suspended ? "suspended" : "active");
          const STATUS_TAG = {
            active: ["Active", "bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30"],
            pending: ["Pending Activation", "bg-yellow-500/15 text-yellow-300 ring-1 ring-yellow-500/40"],
            suspended: ["Suspended", "bg-red-500/15 text-red-400 ring-1 ring-red-500/30"],
          }[statusKey];
          return (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex flex-col gap-1.5 border-b border-white/5 px-4 py-3 text-sm last:border-0 md:grid md:grid-cols-12 md:items-center md:gap-2">
              <div className="col-span-2 flex items-center gap-2 truncate font-mono-metric text-sm font-bold text-primary" data-testid={`member-no-${u.id}`}>
                {u.pending && !isEngineer && <input type="checkbox" data-testid={`select-${u.id}`} checked={selected.includes(u.email)} onChange={() => toggleSelect(u.email)} className="h-4 w-4 shrink-0 accent-primary" />}
                {u.pending ? <span className="text-yellow-400">pending</span> : `#${u.member_no ?? "—"}`}
              </div>
              <div className="col-span-2 truncate">
                <span className="font-medium">{u.name || "—"}</span>
                <span data-testid={`status-tag-${u.id}`} className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_TAG[1]}`}>{STATUS_TAG[0]}</span>
                {!u.pending && <span className="ml-1 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-semibold text-primary">{u.role}</span>}
              </div>
              <div className="col-span-2 truncate text-muted-foreground">{u.email}</div>
              <div className="col-span-2 truncate font-mono-metric text-xs" data-testid={`phone-${u.id}`}>
                {u.phone || (u.phones && u.phones[0]) || <span className="text-muted-foreground">None</span>}
              </div>
              <div className="col-span-2 font-mono-metric font-semibold md:text-right">
                {money(u.wallet_balance_cents)}
                <span className="ml-1 text-[10px] text-emerald-400">({money(u.paid_balance_cents)})</span>
              </div>
              <div className="col-span-2 flex items-center justify-start gap-0.5 md:justify-end">
                {u.pending ? (
                  !isEngineer ? (
                  <>
                    <button data-testid={`activate-pending-${u.id}`} onClick={() => activatePending(u)} title="Activate customer"
                      className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-400 hover:bg-emerald-500/25"><CheckCircle size={14} weight="bold" /> Activate</button>
                    <button data-testid={`delete-pending-${u.id}`} onClick={() => deletePending(u)} title="Remove pending sign-up"
                      className="rounded-lg p-1 text-red-400 hover:bg-white/10"><Trash size={16} /></button>
                  </>
                  ) : null
                ) : (
                  <>
                    {canImpersonate && (
                      <button data-testid={`setup-as-${u.id}`} onClick={() => impersonate(u)} title="Set up as customer (log in to their dashboard)"
                        className="rounded-lg p-1 text-amber-400 hover:bg-white/10"><SignIn size={16} /></button>
                    )}
                    <button data-testid={`view-${u.id}`} onClick={() => openDetail(u)} title="View" className="rounded-lg p-1 hover:bg-white/10"><Eye size={16} /></button>
                    {!isEngineer && (
                      <>
                        <button data-testid={`edit-${u.id}`} onClick={() => openEdit(u)} title="Edit" className="rounded-lg p-1 hover:bg-white/10"><PencilSimple size={16} /></button>
                        <button data-testid={`assign-${u.id}`} onClick={() => openAssign(u)} title="Assign number" className="rounded-lg p-1 text-primary hover:bg-white/10"><Phone size={16} /></button>
                        <button data-testid={`credit-${u.id}`} onClick={() => openCredit(u)} title="Adjust credit" className="rounded-lg p-1 text-emerald-400 hover:bg-white/10"><CurrencyDollar size={16} /></button>
                        {suspended ? (
                          <button data-testid={`activate-${u.id}`} onClick={() => setStatus(u, "active")} title="Activate"
                            className="rounded-lg p-1 text-emerald-400 hover:bg-white/10"><CheckCircle size={16} /></button>
                        ) : (
                          <button data-testid={`suspend-${u.id}`} onClick={() => setStatus(u, "blocked")} title="Suspend / deactivate"
                            className="rounded-lg p-1 text-amber-400 hover:bg-white/10"><Prohibit size={16} /></button>
                        )}
                        <button data-testid={`terminate-${u.id}`} onClick={() => terminateUser(u)} title="Terminate (delete permanently)"
                          className="rounded-lg p-1 text-red-400 hover:bg-white/10"><Trash size={16} /></button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">{users.length === 0 ? "No customers yet." : "No customers match your search."}</div>}
      </div>

      {filtered.length > PER_PAGE && (
        <div className="flex items-center justify-between gap-3 text-sm" data-testid="customers-pagination">
          <span className="text-muted-foreground">
            Showing {(curPage - 1) * PER_PAGE + 1}–{Math.min(curPage * PER_PAGE, filtered.length)} of {filtered.length}
          </span>
          <div className="flex items-center gap-1.5">
            <button data-testid="page-prev" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)}
              className="rounded-full border border-white/10 px-3 py-1.5 font-semibold hover:bg-white/5 disabled:opacity-40">Prev</button>
            <span className="px-2 text-xs text-muted-foreground">Page {curPage} / {totalPages}</span>
            <button data-testid="page-next" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)}
              className="rounded-full border border-white/10 px-3 py-1.5 font-semibold hover:bg-white/5 disabled:opacity-40">Next</button>
          </div>
        </div>
      )}

      {bulk && (
        <Modal title="Bulk invite customers" onClose={() => setBulk(false)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Paste emails separated by commas, spaces, or new lines. Each gets an invite to set their own password. Up to 200 at a time.</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role for all</label>
              <select data-testid="bulk-role" value={bulkRole} onChange={(e) => setBulkRole(e.target.value)}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary">
                {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <textarea data-testid="bulk-emails" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={6}
              placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
              className="w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-xs outline-none focus:border-primary" />
            <button data-testid="bulk-submit" onClick={bulkInvite} disabled={busy}
              className="w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Inviting…" : "Send invites"}</button>
            {bulkResult && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3 text-sm" data-testid="bulk-result">
                <p className="text-emerald-400">Invited: {bulkResult.created.length}</p>
                {bulkResult.skipped.length > 0 && (
                  <div className="mt-1 text-muted-foreground">
                    <p>Skipped: {bulkResult.skipped.length}</p>
                    <ul className="mt-1 max-h-24 overflow-y-auto text-xs">
                      {bulkResult.skipped.map((s2, i) => <li key={i}>{s2.email} — {s2.reason}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>
      )}

      {adding && (
        <Modal title="Add a customer" onClose={() => setAdding(false)}>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Full name</label>
              <input data-testid="new-user-name" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</label>
              <input data-testid="new-user-email" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone (optional)</label>
                <input data-testid="new-user-phone" value={newUser.phone} onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} placeholder="+237…"
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role</label>
                <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary">
                  {roleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Starting credit (USD, optional)</label>
              <input data-testid="new-user-credit" type="number" min="0" step="0.01" value={newUser.credit_dollars} onChange={(e) => setNewUser({ ...newUser, credit_dollars: e.target.value })} placeholder="0.00"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Onboarding</label>
              <div className="mt-1 flex gap-2">
                {[["invite", "Email invite"], ["temp_password", "Set temp password"]].map(([v, l]) => (
                  <button key={v} data-testid={`new-user-mode-${v}`} onClick={() => setNewUser({ ...newUser, mode: v })}
                    className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${newUser.mode === v ? "bg-primary text-primary-foreground" : "border border-border"}`}>{l}</button>
                ))}
              </div>
            </div>
            {newUser.mode === "temp_password" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Temporary password (blank = auto-generate)</label>
                <input data-testid="new-user-temp-password" value={newUser.temp_password} onChange={(e) => setNewUser({ ...newUser, temp_password: e.target.value })} placeholder="min 8 characters"
                  className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
                <p className="mt-1 text-[11px] text-muted-foreground">The customer must change it on first sign-in.</p>
              </div>
            )}
            <button data-testid="create-user-submit" onClick={createUser} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Creating…" : "Create customer"}</button>
          </div>
        </Modal>
      )}

      {created && (
        <Modal title="Customer created" onClose={() => setCreated(null)}>
          <div className="space-y-3 text-sm" data-testid="created-credentials">
            <p className="text-muted-foreground">Account for <span className="font-medium text-foreground">{created.email}</span> is ready.</p>
            {created.temp_password && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Temporary password</p>
                <div className="mt-1 flex items-center gap-2">
                  <code data-testid="temp-password-value" className="flex-1 rounded bg-background px-2 py-1 font-mono-metric text-sm">{created.temp_password}</code>
                  <button onClick={() => { navigator.clipboard.writeText(created.temp_password); toast.success("Copied"); }} className="rounded-lg p-1.5 hover:bg-white/10"><Copy size={16} /></button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Share this securely. They must change it on first sign-in.</p>
              </div>
            )}
            {created.invite_link && (
              <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Invite link (also emailed)</p>
                <div className="mt-1 flex items-center gap-2">
                  <code data-testid="invite-link-value" className="flex-1 truncate rounded bg-background px-2 py-1 text-xs">{created.invite_link}</code>
                  <button onClick={() => { navigator.clipboard.writeText(created.invite_link); toast.success("Copied"); }} className="rounded-lg p-1.5 hover:bg-white/10"><Copy size={16} /></button>
                </div>
              </div>
            )}
            <button onClick={() => setCreated(null)} className="mt-2 w-full rounded-full border border-border py-2.5 text-sm font-semibold">Done</button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal title={`Edit ${editing.email}`} onClose={() => setEditing(null)}>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Name</label>
              <input data-testid="edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Email</label>
              <input data-testid="edit-email" type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone (E.164)</label>
              <input data-testid="edit-phone" value={form.notify_phone || ""} onChange={(e) => setForm({ ...form, notify_phone: e.target.value })} placeholder="+12025550123"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Role</label>
              <select data-testid="edit-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</label>
              <select data-testid="edit-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary">
                <option value="active">Active</option>
                <option value="blocked">Suspended</option>
              </select>
            </div>
            <button data-testid="save-edit" onClick={saveEdit} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </Modal>
      )}

      {crediting && (
        <Modal title={`Adjust balance · ${crediting.email}`} onClose={() => setCrediting(null)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Current: <span className="font-mono-metric text-foreground">{money(crediting.wallet_balance_cents)}</span> · paid <span className="font-mono-metric text-emerald-400">{money(crediting.paid_balance_cents)}</span></p>
            <div className="flex gap-2">
              {["add", "subtract"].map((d) => (
                <button key={d} data-testid={`credit-dir-${d}`} onClick={() => setCredit({ ...credit, direction: d })}
                  className={`flex-1 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${credit.direction === d ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                  {d === "add" ? "Add credit" : "Subtract"}
                </button>
              ))}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Amount (USD)</label>
              <input data-testid="credit-amount" type="number" min="0" step="0.01" value={credit.amount} onChange={(e) => setCredit({ ...credit, amount: e.target.value })} placeholder="10.00"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Reason (optional)</label>
              <input data-testid="credit-reason" value={credit.reason} onChange={(e) => setCredit({ ...credit, reason: e.target.value })} placeholder="Goodwill credit"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <button data-testid="save-credit" onClick={saveCredit} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Applying…" : `${credit.direction === "add" ? "Add" : "Subtract"} funds`}</button>
          </div>
        </Modal>
      )}

      {assigning && (
        <Modal title={`Assign number · ${assigning.email}`} onClose={() => setAssigning(null)}>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Give this customer a phone number. It appears on their account and in the Command Center customer list. <span className="font-mono-metric text-primary">#{assigning.member_no}</span></p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Phone number (E.164)</label>
              <input data-testid="assign-number-input" value={assignForm.number} onChange={(e) => setAssignForm({ ...assignForm, number: e.target.value })} placeholder="+12025551234"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 font-mono-metric text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Country</label>
              <input data-testid="assign-country" value={assignForm.country} onChange={(e) => setAssignForm({ ...assignForm, country: e.target.value })} placeholder="US"
                className="mt-1 w-full rounded-xl border border-input bg-card px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <button data-testid="assign-number-submit" onClick={assignNumber} disabled={busy}
              className="mt-2 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-60">{busy ? "Assigning…" : "Assign number"}</button>
          </div>
        </Modal>
      )}

      {detail && (
        <Modal title={`Customer · ${detail.user.email}`} onClose={() => setDetail(null)}>
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Role</span><div className="font-medium">{detail.user.role}</div></div>
              <div><span className="text-muted-foreground">Status</span><div className="font-medium">{detail.user.status || "active"}</div></div>
              <div><span className="text-muted-foreground">Balance</span><div className="font-mono-metric font-medium">{money(detail.wallet.balance_cents || 0)}</div></div>
              <div><span className="text-muted-foreground">Paid</span><div className="font-mono-metric font-medium text-emerald-400">{money(detail.wallet.paid_balance_cents || 0)}</div></div>
            </div>
            <Section title={`Numbers (${detail.dids.length})`} items={detail.dids.map((d) => `${d.number} · ${d.country || ""}`)} />
            <Section title={`Recent calls (${detail.calls.length})`} items={detail.calls.map((c) => `${c.number} · ${c.destination || ""} · ${c.status}`)} />
            <Section title={`Messages (${detail.sms.length})`} items={detail.sms.map((m) => `${m.direction === "outbound" ? "→ " + m.to : "← " + m.from_number}: ${(m.text || "").slice(0, 40)}`)} />
            <Section title={`Voicemails (${detail.voicemails.length})`} items={detail.voicemails.map((v) => `${v.from_number || "Unknown"} · ${(v.created_at || "").slice(0, 16).replace("T", " ")}`)} />
            <Section title={`Transactions (${detail.transactions.length})`} items={detail.transactions.map((t) => `${t.direction} ${money(t.amount_cents || t.customer_price_cents || 0)} · ${t.product || ""}`)} />
          </div>
        </Modal>
      )}
    </div>
  );
}
